import type {
  ProjectOnboarding,
  ProjectTask,
  TaskComment,
  TaskGroup,
  TaskStatus,
  TimberProject,
} from '../types';
import { onboardingItemToProjectTask } from './onboardingTaskBridge';
import { ACTION_REQUIRED_EXTERNAL_SENTINEL } from './actionRequiredDisplay';
import { normalizeTaskAssignedTo } from './projectTaskUsers';
import type { ReadReceiptMap } from './taskReadReceipts';

/** Consolidated comment scope for a group, unified across a deal's projects (precon + under contract). */
export interface GroupCommentContext {
  /** Stable cross-project key: `${dealId||projectId}::${normalizedGroupName}`. */
  groupKey: string;
  /** Human-readable group name shown in the popup header. */
  displayName: string;
  /** All sibling project ids contributing comments to this group. */
  projectIds: string[];
  /** All taskGroups (across sibling projects) with this normalized name. */
  groupIds: string[];
  /** Every member task id (Firestore + onboarding-mirrored) in those groups. */
  taskIds: string[];
  /** Union of assignees + action-required users across member tasks (notification targets). */
  notifyUserIds: string[];
  /** Project the composer posts under (the clicked group's project). */
  primaryProjectId: string;
  /** The clicked group's id (context for new comments). */
  primaryGroupId: string;
}

/** Lowercased/trimmed group name used to match the same group across phases. */
export function normalizeGroupName(name: string | undefined): string {
  return (name || '').trim().toLowerCase();
}

/** Stable cross-project key for a group thread. */
export function buildGroupCommentKey(dealOrProjectId: string, groupName: string | undefined): string {
  return `${dealOrProjectId}::${normalizeGroupName(groupName)}`;
}

/** All onboarding-mirrored pseudo-tasks for a project (so checklist comments roll up too). */
function onboardingTasksForProject(
  projectId: string,
  projectGroups: TaskGroup[],
  projectOnboardings: ProjectOnboarding[],
): ProjectTask[] {
  const onboardingDoc = projectOnboardings.find((ob) => ob.projectId === projectId);
  if (!onboardingDoc?.items?.length) return [];
  return onboardingDoc.items.map((item) => onboardingItemToProjectTask(item, projectId, projectGroups));
}

function projectPhaseLabel(project: TimberProject | undefined): string {
  return project?.phase === 'preconstruction' ? 'Precon' : 'Contract';
}

/** Member tasks for a group context (Firestore + onboarding-mirrored). */
export function collectGroupMemberTasks(
  ctx: GroupCommentContext,
  projectTasks: ProjectTask[],
  taskGroups: TaskGroup[],
  projectOnboardings: ProjectOnboarding[],
): ProjectTask[] {
  const groupIdSet = new Set(ctx.groupIds);
  const memberTasks: ProjectTask[] = [];
  for (const pid of ctx.projectIds) {
    const projectGroups = taskGroups.filter((g) => g.projectId === pid);
    const firestoreTasks = projectTasks.filter(
      (t) => t.projectId === pid && t.groupId && groupIdSet.has(t.groupId),
    );
    const onboardingTasks = onboardingTasksForProject(pid, projectGroups, projectOnboardings).filter(
      (t) => t.groupId && groupIdSet.has(t.groupId),
    );
    memberTasks.push(...firestoreTasks, ...onboardingTasks);
  }
  return memberTasks;
}

export type GroupMemberTaskRow = {
  taskId: string;
  title: string;
  status: TaskStatus;
  actionRequired?: string;
  actionRequiredNote?: string;
  phaseLabel?: string;
};

/** Compact task rows for the group-comment Action Required strip. */
export function buildGroupMemberTasks(
  ctx: GroupCommentContext,
  projectTasks: ProjectTask[],
  timberProjects: TimberProject[],
  taskGroups: TaskGroup[],
  projectOnboardings: ProjectOnboarding[],
): GroupMemberTaskRow[] {
  const memberTasks = collectGroupMemberTasks(ctx, projectTasks, taskGroups, projectOnboardings);
  const multiPhase = ctx.projectIds.length > 1;
  const projectById = new Map(timberProjects.filter((p) => p.id).map((p) => [p.id!, p]));

  const rows: GroupMemberTaskRow[] = [];
  const seen = new Set<string>();
  for (const t of memberTasks) {
    if (!t.id || seen.has(t.id)) continue;
    seen.add(t.id);
    rows.push({
      taskId: t.id,
      title: (t.title || '').trim() || 'Untitled task',
      status: t.status,
      actionRequired: t.actionRequired,
      actionRequiredNote: t.actionRequiredNote,
      phaseLabel: multiPhase ? projectPhaseLabel(projectById.get(t.projectId)) : undefined,
    });
  }

  return rows.sort((a, b) => {
    const ta = memberTasks.find((t) => t.id === a.taskId);
    const tb = memberTasks.find((t) => t.id === b.taskId);
    const pa = ta?.position ?? 0;
    const pb = tb?.position ?? 0;
    if (pa !== pb) return pa - pb;
    return a.title.localeCompare(b.title);
  });
}

/**
 * Build the consolidated comment context for a clicked group, unifying the same-named group
 * across every project that belongs to the same deal (Preconstruction + Under Contract).
 */
export function collectGroupCommentContext(
  clickedGroup: TaskGroup,
  currentProject: TimberProject | undefined,
  timberProjects: TimberProject[],
  taskGroups: TaskGroup[],
  projectTasks: ProjectTask[],
  projectOnboardings: ProjectOnboarding[],
): GroupCommentContext {
  const normName = normalizeGroupName(clickedGroup.name);
  const dealId = currentProject?.dealId?.trim() || '';
  const keyBase = dealId || currentProject?.id || clickedGroup.projectId;
  const groupKey = buildGroupCommentKey(keyBase, clickedGroup.name);

  // Sibling projects = same deal (fallback to just the clicked group's project).
  const siblingProjects = dealId
    ? timberProjects.filter((p) => p.id && p.dealId?.trim() === dealId)
    : timberProjects.filter((p) => p.id === clickedGroup.projectId);
  const projectIds = Array.from(
    new Set(
      (siblingProjects.length ? siblingProjects.map((p) => p.id!) : [clickedGroup.projectId]).filter(
        Boolean,
      ),
    ),
  );

  // All groups (across sibling projects) whose normalized name matches.
  const matchingGroups = taskGroups.filter(
    (g) => g.id && projectIds.includes(g.projectId) && normalizeGroupName(g.name) === normName,
  );
  const groupIds = Array.from(new Set(matchingGroups.map((g) => g.id!)));

  const ctxForMembers: GroupCommentContext = {
    groupKey,
    displayName: clickedGroup.name,
    projectIds,
    groupIds,
    taskIds: [],
    notifyUserIds: [],
    primaryProjectId: clickedGroup.projectId,
    primaryGroupId: clickedGroup.id || '',
  };
  const memberTasks = collectGroupMemberTasks(
    ctxForMembers,
    projectTasks,
    taskGroups,
    projectOnboardings,
  );

  const taskIds = Array.from(new Set(memberTasks.map((t) => t.id).filter(Boolean) as string[]));

  const notify = new Set<string>();
  for (const t of memberTasks) {
    for (const uid of normalizeTaskAssignedTo(t)) notify.add(uid);
    if (t.actionRequired && t.actionRequired !== ACTION_REQUIRED_EXTERNAL_SENTINEL) {
      notify.add(t.actionRequired);
    }
  }

  return {
    groupKey,
    displayName: clickedGroup.name,
    projectIds,
    groupIds,
    taskIds,
    notifyUserIds: Array.from(notify),
    primaryProjectId: clickedGroup.projectId,
    primaryGroupId: clickedGroup.id || '',
  };
}

/**
 * Build a group context starting from a task (used by Architect/Engineer portals,
 * which list tasks rather than group headers).
 */
export function buildGroupContextForTask(
  task: ProjectTask,
  timberProjects: TimberProject[],
  taskGroups: TaskGroup[],
  projectTasks: ProjectTask[],
  projectOnboardings: ProjectOnboarding[],
): GroupCommentContext | null {
  if (!task.groupId) return null;
  const group = taskGroups.find((g) => g.id === task.groupId && g.projectId === task.projectId);
  if (!group) return null;
  const currentProject = timberProjects.find((p) => p.id === task.projectId);
  return collectGroupCommentContext(
    group,
    currentProject,
    timberProjects,
    taskGroups,
    projectTasks,
    projectOnboardings,
  );
}

/** Comments belonging to this group: by member taskId or by the new groupCommentKey. */
export function aggregateGroupComments(
  allTaskComments: TaskComment[],
  ctx: GroupCommentContext,
): TaskComment[] {
  const taskIdSet = new Set(ctx.taskIds);
  return allTaskComments
    .filter((c) => c.groupCommentKey === ctx.groupKey || (c.taskId && taskIdSet.has(c.taskId)))
    .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
}

function commentCreatedMs(comment: TaskComment): number {
  const t = comment.createdAt instanceof Date
    ? comment.createdAt.getTime()
    : new Date(comment.createdAt).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Group on this project whose thread has the newest comment.
 * Includes comments posted on the group and on its tasks. Groups with no comments are skipped.
 */
export function groupWithLatestComment(input: {
  groups: TaskGroup[];
  currentProject: TimberProject | undefined;
  timberProjects: TimberProject[];
  taskGroups: TaskGroup[];
  projectTasks: ProjectTask[];
  projectOnboardings: ProjectOnboarding[];
  taskComments: TaskComment[];
}): TaskGroup | null {
  let best: { group: TaskGroup; ms: number } | null = null;
  for (const group of input.groups) {
    if (!group.id) continue;
    const ctx = collectGroupCommentContext(
      group,
      input.currentProject,
      input.timberProjects,
      input.taskGroups,
      input.projectTasks,
      input.projectOnboardings,
    );
    let latest = 0;
    for (const comment of aggregateGroupComments(input.taskComments, ctx)) {
      const ms = commentCreatedMs(comment);
      if (ms > latest) latest = ms;
    }
    if (latest <= 0) continue;
    if (!best || latest > best.ms) best = { group, ms: latest };
  }
  return best?.group ?? null;
}

/** Epoch ms of the latest comment not authored by `userId` (0 if none). */
function latestForeignCommentMs(comments: TaskComment[], userId: string): number {
  let best = 0;
  for (const c of comments) {
    if (c.authorId === userId) continue;
    const t = c.createdAt instanceof Date ? c.createdAt.getTime() : new Date(c.createdAt).getTime();
    if (!Number.isNaN(t) && t > best) best = t;
  }
  return best;
}

/** Latest comment from someone else in this group thread (for list previews). */
export function latestForeignGroupComment(
  comments: TaskComment[],
  ctx: GroupCommentContext,
  userId: string,
): { ms: number; author: string; text: string } | null {
  const grouped = aggregateGroupComments(comments, ctx);
  let best: { ms: number; author: string; text: string } | null = null;
  for (const c of grouped) {
    if (c.authorId === userId) continue;
    const t = c.createdAt instanceof Date ? c.createdAt.getTime() : new Date(c.createdAt).getTime();
    if (Number.isNaN(t)) continue;
    if (!best || t > best.ms) {
      best = {
        ms: t,
        author: c.authorName || '',
        text: c.bodyHtml || c.body || '',
      };
    }
  }
  return best;
}

/** True when the group has comment activity (from others) newer than the user's read receipt. */
export function groupHasUnread(
  comments: TaskComment[],
  ctx: GroupCommentContext,
  userId: string,
  receipts: ReadReceiptMap,
): boolean {
  if (!userId) return false;
  const latest = latestForeignCommentMs(comments, userId);
  if (latest === 0) return false;
  const readAt = receipts[ctx.groupKey] || 0;
  return latest > readAt;
}
